// One running Munos script on a multi-script client — a "tenant" in the
// supervisor/tenant model (docs/multi-script-client.md). A tenant owns
// everything script-private: its compiled WASM module + linear memory,
// its WebSocket connection, its persistence namespace, its input view,
// its loaded images, and its per-frame draw calls. It does NOT own the
// emulated game — RAM access and ROM pause are shared-game effects the
// supervisor mediates, injected here as a TenantContext.
//
// Several tenants run side by side over one core under one MunosScriptHost
// (munos-emulator-host.ts). A tenant has no verb that reaches a sibling:
// hop / disconnect are scoped to itself, so mods cannot eject each other.
//
// This is the per-script half of what used to be MunosClient +
// MunosEmulatorHost fused by inheritance. The emulator-host half (core,
// frame loop, framebuffer, render compositing, pause refcount, input
// fan-out) lives in the supervisor.

import { defaultCompiler } from "./compiler-default.js";
import {
  type MunosCompiler,
  CompileError,
  type DrawCall,
  type SoundCall,
  type IndexedPng,
  type HookCoord,
  AsyncOpQueue,
  InputLatch,
  ImageStore,
  makeBaseEnv,
  makeClientCommonEnv,
  resolveRedirectUrl,
  decodeBitmap,
  decodePngToIndexed,
  extractIndexedRegion,
  decodeImageLiteral,
  decodeImageWireBytes,
  assertImageDims,
  composeMemRead,
  spreadMemWrite,
  paintDrawCalls,
  arrData,
  bump,
  heapMark,
  heapRewind,
  EscapedHeap,
  setupEscapedAfterInit,
  growEscapedRegion,
  allocArray,
  encodeBytes,
  readBytesPtr,
  readStringPtr,
} from "@multinostalgia/munos";
import { parseVgm, type VgmFile } from "@multinostalgia/core-contract";
import {
  LocalStorageMunosKv,
  type MunosKvBackend,
} from "./persistence-store.js";
import {
  DefaultFetchAdapter,
  type FetchAdapter,
} from "./munos-fetch.js";
import { type BusConnection, type BusRider } from "./bus-connection.js";

export type { DrawCall };
export { LocalStorageMunosKv, type MunosKvBackend } from "./persistence-store.js";
export { DefaultFetchAdapter, type FetchAdapter, type FetchResult } from "./munos-fetch.js";

export type StatusListener = (kind: "info" | "error" | "connected", message: string) => void;


// The bus key for the in-browser local server (singleplayer). A tenant marked
// `localBus` joins THIS bus instead of one keyed by its own URL, so a launched
// set (e.g. smb_sender + smb_recorder) all ride one shared loopback bus served
// by the supervisor's LocalServer — the "your machine is its own server" model
// (docs/multi-script-client.md §"The lobby's role"). The supervisor recognizes
// this key in attachToBus and backs it with a LoopbackBusSocket.
export const LOCAL_BUS_KEY = "local:singleplayer";

// The bus key for a P2P JOINER (docs/p2p.md). Mirrors LOCAL_BUS_KEY: a tenant
// marked `joinBus` joins THIS one shared bus rather than one keyed by its own
// URL, so a joiner's whole mod group (e.g. smb_sender + smb_recorder) rides ONE
// WebRTC connection to the host and shows up as a single peer/slot — not one
// connection per mod URL. The supervisor backs this key with the join socket
// factory (a WebRtcBusSocket signaling through the broker to the host).
export const JOIN_BUS_KEY = "p2p:host";

// Description of the deployment a hop is targeting. Returned by the
// resolver the host provides. `null` means "same deployment as the one
// we're currently in" — the tenant stays on the existing wasm and just
// reopens its WebSocket against the new URL. For different-deployment
// hops the resolver returns the new script's source text.
//
// `label` is the destination script's identity (its .munos basename, as
// the serving server reports it). It becomes the script-id half of the
// persistence scope after the hop, so the destination sees ITS data, not
// the deployment's-by-URL — see docs/persistence.md §"What identifies a
// script".
export interface HopDestination {
  source: string;
  label: string;
}

export type DestinationResolver =
  (url: string) => Promise<HopDestination | null>;

// Resolver for a GROUP hop: fetches the FULL array of scripts a destination
// endpoint serves (one entry per member — its source + identity label), so the
// supervisor can reconcile the running set against it. A bare single-script
// endpoint resolves to a one-element array. Returns null on failure.
export type DeploymentResolver =
  (url: string) => Promise<readonly HopDestination[] | null>;

// The shared-game surface a tenant cannot own, injected by the supervisor.
// RAM and pause are properties of the one emulated cartridge, not of any
// single script (docs/multi-script-client.md §"The boundary").
export interface TenantContext {
  // CPU RAM access, routed to the one shared core. Many tenants reading
  // is fine; two writing the same address is an accepted co-tenant
  // interaction (unarbitrated).
  readCpuByte(addr: number): number;
  writeCpuByte(addr: number, value: number): void;
  // ROM pause is refcounted across tenants by the supervisor: the game
  // runs only while zero tenants hold a pause. acquire/release are
  // balanced by the tenant's own one-shot guard, so each tenant
  // contributes at most one to the count.
  acquirePause(): void;
  releasePause(): void;
  // Native screen dimensions of the shared core.
  readonly screenW: number;
  readonly screenH: number;
  // Status sink (connecting / connected / error), surfaced per tenant.
  readonly onStatus: StatusListener;
  // Resolver for spawn_local: fetches a single child mod's source. null ⇒
  // spawning is unavailable. (Group hops don't use this — they go through
  // hopGroup, which fetches the destination's whole served array.)
  readonly resolveDestination: DestinationResolver | null;
  // What max_clients() reports to the script.
  readonly maxClients: number;
  // Hex SHA-1 of the loaded ROM, returned by local_rom_hash().
  readonly romHash: string;

  // ----- execution hooks (shared game, optional capability) -----
  //
  // on_exec / clear_exec / read_reg / write_reg / write_rom all act on the
  // one shared core, so they route through the supervisor like RAM access
  // does. They no-op (return 0) when the core has no execution-hook
  // support. on_exec registers a CORE hook whose handler must re-enter
  // THIS tenant's wasm, so the tenant passes its own `fire` closure; the
  // supervisor returns the core handle unchanged. See
  // docs/execution-hooks-and-bank-switching.md.
  onExecHook(target: HookCoord, fire: (pc: number) => void, priority?: number): number;
  clearExecHook(handle: number): void;
  readReg(reg: number): number;
  writeReg(reg: number, val: number): void;
  writeRom(coord: HookCoord, byte: number): void;
  readPpu(addr: number): number;
  writePpu(addr: number, value: number): void;

  // ----- spawning sibling/child mods (the explicit orchestrator door) -----
  //
  // spawnTenant fetches the script at `url` (via the hop resolver), runs it
  // as a child of `parent` over the same game, and returns a handle
  // immediately (the load is async; the handle is despawnable while
  // loading). despawnTenant tears down a tenant the `requester` owns — a
  // descendant in its subtree — and that tenant's own descendants. A script
  // can only reach its own subtree, never a sibling: mods can't eject each
  // other (docs/multi-script-client.md). Returns 0 if spawning is
  // unavailable (no resolver wired).
  // `local` marks a spawn that should run on the in-browser LocalServer's
  // shared bus (singleplayer): the supervisor stands the server up, hosts the
  // spawned source's server half on it, and the new tenant rides the loopback
  // bus. The URL still names the real origin (fetch + persistence). Defaults
  // to a networked spawn.
  // `initData` seeds every mounted member's `event init` — the per-INSTANCE
  // spawn payload, the local-mount mirror of the server's spawn(..., data,...)
  // (docs/level-player-sessions.md §4). `handoff` remains per-PLAYER (→ each
  // member's `event connect`). Omitted / empty ⇒ members init with no seed.
  spawnTenant(url: string, handoff: Uint8Array, parent: ScriptTenant, local?: boolean, initData?: Uint8Array): number;
  despawnTenant(handle: number, requester: ScriptTenant): void;

  // ----- group hop (switching the shared connection's endpoint) -----
  //
  // The shared bus has ONE endpoint, so hopping is a GROUP operation: any
  // tenant's hop() (and a server-pushed redirect) re-points the whole bus. The
  // supervisor fetches the destination's served array and reconciles the
  // running tenants against it — evicting scripts no longer served, keeping the
  // byte-identical overlap running seamlessly, and starting the newly-served
  // ones. See docs/multi-script-client.md §"Hopping".
  hopGroup(url: string, handoff: Uint8Array): void;

  // ----- shared-bus transport (docs/multi-script-client.md) -----
  //
  // The supervisor owns one BusConnection per server URL. A tenant attaches
  // as a rider on connect (attachToBus joins or creates the bus for `url` and
  // returns it so the tenant can send through it) and detaches on stop/hop
  // (detachFromBus leaves it; the supervisor closes the socket once the last
  // rider is gone).
  attachToBus(rider: BusRider, url: string): BusConnection;
  detachFromBus(rider: BusRider, bus: BusConnection): void;

  // ----- sound (shared audio output, docs/sound.md) -----
  //
  // Audio is a property of the one game — there is a single output stream —
  // so playback routes through the supervisor. A tenant parses its own VGM
  // assets (private handles) but hands the parsed file here to actually
  // voice it. v1 is a single shared voice: a new play replaces the previous,
  // across all tenants (the multi-bus mixer is deferred — docs/sound.md
  // §11). No-op when the host has no synth-core factory (silent, harmless).
  playSound(file: VgmFile, loop: boolean): void;

  // ----- battery save (shared game, docs/battery-save-persistence.md) -----
  //
  // The cartridge's battery-backed save file is host-managed and persisted
  // locally by default. A mod that owns the save itself (e.g. a cloud-save mod
  // that loads/stores it over the db_* store) calls this to turn the host's
  // automatic write-back off; passing true turns it back on. It is a
  // shared-game latch — the save is one file for the one game — so the last
  // tenant to set it wins for all of them.
  setBatteryPersist(enabled: boolean): void;
}

export interface TenantSpec {
  // Munos script source text (the same source the server compiled).
  source: string;
  // WebSocket URL of the server shard this tenant talks to.
  url: string;
  // The script's identity — its .munos basename, as reported by the
  // server in the source-fetch JSON envelope (`scripts[].label`). Paired
  // with the URL's origin to scope this tenant's client-side persistence,
  // so two mods on one deployment get distinct scopes. See
  // docs/persistence.md §"What identifies a script".
  scriptLabel: string;
  // Initial handoff payload sent as the first WebSocket frame. Default empty.
  handoff?: Uint8Array;
  // Backend for the persistence builtins. When omitted, a LocalStorageMunosKv
  // is constructed; the supervisor is responsible for handing in a
  // per-tenant-scoped store so two mods on one origin don't collide.
  persistence?: MunosKvBackend;
  // Adapter for the `fetch` builtin. When omitted, a DefaultFetchAdapter.
  fetch?: FetchAdapter;
  // When true, this tenant rides the shared LOCAL bus (the in-browser
  // LocalServer) instead of a bus keyed by its own URL — a singleplayer mod
  // launched via `spawn_local("local:/…")`. Its `url` still names the real
  // origin (source fetch + persistence scope are unchanged); only the bus is
  // local. See LOCAL_BUS_KEY and docs/multi-script-client.md.
  localBus?: boolean;
  // When true, this tenant is a P2P JOINER and rides the shared JOIN bus (one
  // WebRTC connection to the sharing host) instead of a bus keyed by its own
  // URL, so the joiner's whole mod group is one peer. Its `url` still names the
  // real origin (source fetch + persistence scope unchanged); only the bus is
  // the P2P host. Mutually exclusive with localBus. See JOIN_BUS_KEY.
  joinBus?: boolean;
  // Compiler backend. Omitted in production/tests (the in-process TypeScript
  // compiler); the Rust/WASM backend is injected here once proven
  // (docs/rust-compiler-migration.md).
  compiler?: MunosCompiler;
}

// Upper bound on distinct decoded PNGs kept in a tenant's cache. Mods use a
// handful of atlases; the cap only matters for a pathological per-frame-varying
// source, whose oldest entries are evicted rather than growing without limit.
const MAX_PNG_CACHE = 256;

// Content-addressed cache key for decoded PNG bytes: FNV-1a over the bytes plus
// their length. Cheap (one xor + multiply per byte, far less than a PNG decode)
// and keyed by CONTENT so a reused wasm heap pointer holding new bytes can't
// alias an older decode — the failure mode of the previous pointer-keyed cache.
function pngContentKey(bytes: Uint8Array): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i]!;
    h = Math.imul(h, 0x01000193);
  }
  return `${bytes.length}:${(h >>> 0).toString(16)}`;
}

export class ScriptTenant implements BusRider {
  // Public on the structural HeapHost interface used by memory-utils:
  // the tenant owns the wasm linear memory.
  memory!: WebAssembly.Memory;
  view!: DataView;
  heapPtrGlobal!: WebAssembly.Global;

  // Exported client entry-point names (e.g. "event_client_frame") the compiler
  // proved rewindable — invokeEvent brackets them with heapMark/heapRewind to
  // reclaim their transient heap. Matters most for `event frame`, which fires
  // at 60 Hz (docs/memory-model.md §"Reclamation, staged"). Set in start().
  private rewindableExports: ReadonlySet<string> = new Set();
  // The escaped generation for this tenant, present only when its module was
  // compiled with the stage-2 partition. Bound into the promote/retain/release
  // write-barrier imports (docs/memory-model.md §"Stage 2").
  private escapedHeap?: EscapedHeap;

  protected instance!: WebAssembly.Instance;
  // True once instantiate() has set `instance` so the wasm is callable. A
  // tenant is pushed into the host's list the moment it's registered, but its
  // wasm instantiates asynchronously inside start(); a runtime spawn or a
  // group hop registers a tenant while the frame loop is already running, so
  // fireAllTenants can reach it in the window before start() completes. Event
  // dispatch is gated on this so a not-yet-instantiated tenant is skipped
  // instead of dereferencing an undefined `instance`. (The initial tenant set
  // can't hit the window: host.start() awaits every start() before
  // startFrameLoop.)
  protected ready = false;
  // Whether this tenant rides the shared in-browser LocalServer bus
  // (singleplayer) rather than a bus keyed by its own URL. Set from
  // spec.localBus; consulted in connect(). See LOCAL_BUS_KEY. Not readonly:
  // rehomeLocal() flips it when a host_locally handoff moves a remote-launched
  // tenant onto the in-page loopback (docs/node-federation.md).
  protected localBus: boolean;
  // Whether this tenant rides the shared P2P JOIN bus (one connection to the
  // sharing host) rather than a bus keyed by its own URL. See JOIN_BUS_KEY.
  protected readonly joinBus: boolean;
  // The shared bus this tenant currently rides (null before connect / after
  // detach). The supervisor owns it; the tenant only sends through it and
  // receives broadcasts from it.
  protected bus: BusConnection | null = null;
  protected frameNum = 0;
  protected stopped = false;

  // Set true after the script calls disconnect(). The tenant stops firing
  // event_client_* into the wasm and its WS-close handler suppresses the
  // "disconnected" status; the supervisor keeps stepping the ROM and the
  // tenant just stops being in the picture. Sibling tenants are untouched.
  protected scriptUnloaded = false;
  // Shared-bus channels this tenant's client half listens on (from its `listen`
  // declarations, via register_channel after init). dispatchReceive delivers a
  // frame to this tenant only if its channel is in this set, so a co-tenant on
  // the shared bus never sees another's traffic.
  private readonly channels = new Set<number>();
  // True when this tenant's `event receive` declared the optional leading
  // `channel: i32` param; dispatchReceive then passes the routed channel to it.
  private receiveWantsChannel = false;

  // `source` is mutable: an inter-deployment hop swaps it before recompiling.
  protected source: string;
  protected url: string;
  // The running script's identity (its .munos basename). Mutable across
  // hops alongside source/url. Paired with the URL's origin to form the
  // persistence scope, so the script — not the endpoint that served it —
  // scopes its data.
  protected scriptLabel: string;
  protected handoffBytes: Uint8Array;
  // Circuit breaker for a persistently-throwing event handler. A script whose
  // event traps on EVERY frame would otherwise (a) flood the console with one
  // console.error per frame — enough to freeze the tab — and (b) burn CPU
  // re-entering dead wasm forever. We log the FIRST failure in full, then stay
  // quiet; after DISABLE_AFTER CONSECUTIVE failures we stop invoking that handler
  // for this tenant (one final "disabled" line). Any success resets the streak, so
  // a transient / data-dependent throw recovers and is never disabled.
  private static readonly DISABLE_AFTER = 60;
  private readonly eventFailStreak = new Map<string, number>();
  private readonly disabledEvents = new Set<string>();
  // BusRider: the handoff sent to the server on (re)connect. Mutable across
  // hops (a different-deployment hop replaces it).
  get handoff(): Uint8Array { return this.handoffBytes; }

  // The running script's identity + source, read by the supervisor to diff the
  // running set against a hop destination's served array (label = identity;
  // source compared byte-for-byte to decide seamless-keep vs evict+restart).
  get label(): string { return this.scriptLabel; }
  get sourceText(): string { return this.source; }

  protected readonly ctx: TenantContext;

  // ----- input -----
  // Frame-stable polling snapshots + consumed-press auto-consumes-release
  // (docs/language.md §Input). The supervisor fans each gamepad edge to
  // every tenant's latch (non-exclusive reads); consume_input only
  // suppresses propagation to the ROM, never to a sibling.
  protected readonly input = new InputLatch({
    dispatch: (button, pressed) => {
      if (this.scriptUnloaded) return;
      this.invokeEvent("event_client_input", button, pressed ? 1 : 0);
    },
    afterDispatch: () => this.drainPendingDb(),
  });

  // Whether THIS tenant currently holds a ROM-pause request. One-shot
  // guard so the tenant contributes at most one to the supervisor's
  // refcount (matching the old idempotent pause_rom / resume_rom).
  protected selfPauseRequested = false;

  // Persistence + fetch — per-tenant, live and die with the tenant.
  protected persistence: MunosKvBackend;
  protected fetchAdapter: FetchAdapter;

  // Pending completions for the persistence + fetch builtins; drained at
  // safe points between event handlers.
  private readonly asyncOps = new AsyncOpQueue(this, () => this.instance.exports, {
    label: "munos",
    role: "client",
  });

  // ----- images & draw calls (script-private) -----
  private readonly images = new ImageStore();
  // Decoded-PNG cache keyed by the bytes-pointer the script holds, so a
  // script that stashes base64_decode() in a const hits the cache.
  private readonly pngBytesCache = new Map<string, IndexedPng>();
  // Parsed VGM assets this tenant created via sound_from_vgm. The handle
  // returned to the script is the 1-based index (0 = the null handle).
  // Tenant-private: each script has its own handle space, like its images.
  private readonly sounds: VgmFile[] = [];

  // This frame's draw calls. The supervisor reads them after the frame
  // event fires, composites all tenants' calls in load order, then clears.
  private pendingDrawCalls: DrawCall[] = [];

  private readonly compiler: MunosCompiler;

  constructor(ctx: TenantContext, spec: TenantSpec) {
    this.ctx = ctx;
    const compiler = spec.compiler ?? defaultCompiler();
    if (compiler === undefined) {
      throw new Error(
        "ScriptTenant requires a compiler backend — the host injects the WASM " +
          "compiler (main.ts/munos-script-host.ts); tests register one via test-setup.ts.",
      );
    }
    this.compiler = compiler;
    this.source = spec.source;
    this.url = spec.url;
    this.localBus = spec.localBus ?? false;
    this.joinBus = spec.joinBus ?? false;
    this.scriptLabel = spec.scriptLabel;
    this.handoffBytes = spec.handoff ?? new Uint8Array();
    // The backend is scope-agnostic; the per-call scope is supplied via
    // makeClientCommonEnv (deriveScopeForPersistence), so it tracks this
    // tenant's identity across hops.
    this.persistence = spec.persistence ?? new LocalStorageMunosKv();
    this.fetchAdapter = spec.fetch ?? new DefaultFetchAdapter();
  }

  // The mod's real origin URL. For a local-bus (host-local) tenant this is still
  // the fetchable source origin — only the bus is local (see spawn_local) — so a
  // P2P share link can carry it to let a joiner fetch the same client half.
  get modUrl(): string { return this.url; }

  // True when this tenant rides the in-browser LocalServer bus (singleplayer /
  // host-local) rather than a bus keyed by its own URL.
  get onLocalBus(): boolean { return this.localBus; }

  // ----- lifecycle -----

  async start(): Promise<void> {
    let compiled;
    try {
      compiled = this.compiler.compile(this.source, "client");
    } catch (e) {
      const msg = e instanceof CompileError ? e.message : String(e);
      const label = this.scriptLabel || "(unnamed script)";
      this.ctx.onStatus("error", `compile failed for '${label}': ${msg}`);
      throw e;
    }
    // Stage 2: a module that reassigns a global to a heap value gets an escaped
    // generation. Constructed with only its base; its cursor/limit are seeded
    // after init has bump-allocated the globals into it (setupEscapedAfterInit).
    // Its barrier-rewindable events are also safe to rewind, but only once that
    // heap backs the barrier — so fold them in only when it's present.
    if (compiled.usesEscapedHeap) {
      this.escapedHeap = new EscapedHeap(compiled.heapStart);
    }
    const rewindNames = [...compiled.rewindableEvents];
    if (this.escapedHeap) rewindNames.push(...compiled.barrierRewindableEvents);
    this.rewindableExports = new Set(rewindNames.map((n) => `event_client_${n}`));
    await this.instantiate(compiled.wasm);
    // init() traps propagate raw deliberately — fail fast on a broken
    // script instead of running over corrupt globals.
    const init = this.instance.exports.init as (() => void) | undefined;
    if (init) init();
    // init bump-allocated the globals at the low end of the escaped generation;
    // seed the escaped heap over them and plant the young base a headroom above
    // (docs/memory-model.md §"Follow-ons"). Must run before any event fires.
    if (this.escapedHeap) setupEscapedAfterInit(this, this.escapedHeap);
    // Collect this tenant's client-half `listen` subscriptions (after init, so
    // a `const` channel global is set). register_channel fills this.channels.
    const reg = this.instance.exports.__register_channels_client as (() => void) | undefined;
    if (reg) reg();
    this.receiveWantsChannel = !!this.instance.exports.__receive_wants_channel_client;
    this.drainPendingDb();
    await this.connect();
  }

  // Tear this tenant down: close its socket, stop firing into its wasm,
  // and release any pause it held so it doesn't pin the shared game.
  // Does NOT touch the supervisor's frame loop or sibling tenants.
  stop(): void {
    this.stopped = true;
    // Abandon any in-flight db_*/fetch completions: their promises resolve on
    // a later microtask and, with the epoch unchanged, would dispatch their
    // callbacks into this now-stopped tenant's wasm (the drain path has no
    // stopped-guard). Bumping the epoch via clear() drops them — the same
    // discipline the server tier applies in ScriptInstance.stop().
    this.asyncOps.clear();
    this.releaseSelfPause();
    this.detachTransport();
  }

  // Leave the shared bus this tenant is riding (if any). The supervisor
  // closes the underlying socket once the last rider leaves, so one mod
  // detaching never pulls the pipe out from under a co-rider.
  protected detachTransport(): void {
    if (this.bus) {
      this.ctx.detachFromBus(this, this.bus);
      this.bus = null;
    }
  }

  // Script-initiated self-unload via the disconnect() builtin. Closes the
  // WS and stops event dispatch, but leaves the supervisor running the
  // ROM and the other tenants alone. Releases any held pause. Idempotent.
  protected performDisconnect(): void {
    if (this.scriptUnloaded) return;
    this.scriptUnloaded = true;
    // As in stop(): drop in-flight async completions so a late db_*/fetch
    // callback can't fire into a script that has unloaded itself.
    this.asyncOps.clear();
    this.releaseSelfPause();
    this.detachTransport();
    this.ctx.onStatus("info", "offline");
  }

  isStopped(): boolean { return this.stopped; }
  isUnloaded(): boolean { return this.scriptUnloaded; }

  // ----- WebAssembly instantiation -----

  protected async instantiate(wasm: Uint8Array): Promise<void> {
    const env = this.buildEnv();
    const mod = await WebAssembly.compile(wasm as BufferSource);
    this.instance = await WebAssembly.instantiate(mod, { env });
    this.memory = this.instance.exports.memory as WebAssembly.Memory;
    this.heapPtrGlobal = this.instance.exports.heap_ptr as WebAssembly.Global;
    this.view = new DataView(this.memory.buffer);
    this.ready = true;   // wasm is now callable — event dispatch may fire
    // A hop swaps the wasm under us; drop completions queued against the
    // old instance since the dispatchers just got replaced.
    this.asyncOps.clear();
  }

  refreshView(): void {
    this.view = new DataView(this.memory.buffer);
  }

  protected bump(bytes: number): number { return bump(this, bytes); }
  protected allocArray(length: number, elementSize: number): number {
    return allocArray(this, length, elementSize);
  }
  protected encodeBytes(bytes: Uint8Array): number { return encodeBytes(this, bytes); }
  protected readBytesPtr(ptr: number): Uint8Array { return readBytesPtr(this, ptr); }
  protected readStringPtr(ptr: number): string { return readStringPtr(this, ptr); }

  // ----- event dispatch -----

  protected invokeEvent(name: string, ...args: number[]): boolean {
    // Skip until the wasm is instantiated (see `ready`): a tenant can be fired
    // by the frame loop in the window after it's registered but before its
    // async start() finishes. Report not-run so fireFrame doesn't advance the
    // frame counter for a frame the script never saw; it catches up next tick.
    if (!this.ready) return false;
    // Circuit broken: this handler has failed every call for a while, so we've
    // stopped invoking it (see the catch below). Stays off until reload.
    if (this.disabledEvents.has(name)) return false;
    const fn = this.instance.exports[name] as ((...a: number[]) => void) | undefined;
    if (!fn) return true;
    // Young-generation rewind: for a rewindable event, snapshot the bump cursor
    // and restore it after, reclaiming every byte the body allocated. Sent data
    // (send_client) and hop handoffs are copied out of guest memory during the
    // call, and image/sound/callback events are non-rewindable by construction,
    // so nothing the host retains points above the mark. See docs/memory-model.md.
    const rewindable = this.rewindableExports.has(name);
    const mark = rewindable ? heapMark(this) : 0;
    try {
      fn(...args);
      this.eventFailStreak.delete(name); // recovered → reset the streak
      return true;
    } catch (e) {
      const n = (this.eventFailStreak.get(name) ?? 0) + 1;
      this.eventFailStreak.set(name, n);
      if (n === 1) {
        // First failure: log it in full. Subsequent identical failures stay quiet
        // so a per-frame trap can't flood the console and freeze the tab.
        console.error(`[munos] ${this.scriptLabel || "?"} ${name} threw:`, e);
      } else if (n === ScriptTenant.DISABLE_AFTER) {
        this.disabledEvents.add(name);
        console.error(
          `[munos] ${this.scriptLabel || "?"} ${name} threw ${n}x in a row — ` +
          `disabling it for this tenant to protect the page. Fix the script and reload.`,
        );
      }
      return false;
    } finally {
      if (rewindable) {
        heapRewind(this, mark);
        // Young is empty at the split — a safe boundary to raise the escaped
        // ceiling if persistent state is outgrowing its region (docs/memory-model.md
        // §"Follow-ons"). No-op when there's headroom.
        if (this.escapedHeap) growEscapedRegion(this, this.escapedHeap);
      }
    }
  }

  protected callEvent(name: string, ...args: number[]): boolean {
    const ok = this.invokeEvent(name, ...args);
    this.drainPendingDb();
    return ok;
  }

  // Deliver one inbound server→client frame: encode into the wasm heap
  // and fire event_client_receive. Shared by the WebSocket message
  // handler and the reflect host's loopback delivery.
  dispatchReceive(bytes: Uint8Array): void {
    // Guard like dispatchPeerLeft/fireFrame: the WebSocket message handler
    // checks this before calling, but the loopback bus path (LocalServer)
    // calls dispatchReceive directly, so a frame still in flight after teardown
    // would re-enter dead wasm without this. Guard BOTH ways the tenant can go
    // inert: script-initiated disconnect() (scriptUnloaded) AND supervisor
    // teardown via stop() (stopped) — the loopback bus iterates a `[...riders]`
    // snapshot, so a rider stop()ed mid-fan-out is still in that snapshot and
    // would otherwise be dispatched into. (fireFrame/pressButton are filtered
    // by the supervisor's isStopped() check; this direct path is not.)
    if (this.stopped || this.scriptUnloaded) return;
    // Channel demux: the frame leads with a u16 channel; deliver the bare body
    // to this tenant only if it `listen`s on that channel.
    const channel = bytes.length >= 2 ? (bytes[0]! | (bytes[1]! << 8)) : -1;
    if (channel < 0 || !this.channels.has(channel)) return;
    const ptr = this.encodeBytes(bytes.subarray(2));
    if (this.receiveWantsChannel) {
      this.callEvent("event_client_receive", channel, ptr);
    } else {
      this.callEvent("event_client_receive", ptr);
    }
  }

  // Fire the script's `event peer_left(slot)` — the interest-management leave
  // signal. Shared by the WebSocket control-message path and the reflect
  // host's loopback. See docs/interest-management.md.
  dispatchPeerLeft(slot: number): void {
    // Same as dispatchReceive: the loopback path calls this directly, so guard
    // both inert states (supervisor stop() and script disconnect()).
    if (this.stopped || this.scriptUnloaded) return;
    this.callEvent("event_client_peer_left", slot);
  }

  // Synchronous re-entry into THIS tenant's wasm when one of its execution
  // hooks fires inside core.frame() (pre-step). Mirrors invokeEvent's
  // direct-call shape; the supervisor handed the core this closure bound to
  // this tenant, so a fired hook can only re-enter the tenant that armed it.
  private invokeExecCallback(cbId: number, pc: number): void {
    const fn = this.instance.exports.__cb_on_exec as
      ((cbId: number, pc: number) => void) | undefined;
    if (!fn) return;
    try {
      fn(cbId, pc);
    } catch (e) {
      console.error(`[munos] __cb_on_exec(${cbId}) threw:`, e);
    }
  }

  // ----- host bindings -----

  protected buildEnv(): WebAssembly.Imports["env"] {
    return {
      ...makeBaseEnv(this, {
        label: "munos",
        ...(this.escapedHeap !== undefined ? { escapedHeap: this.escapedHeap } : {}),
      }),

      ...makeClientCommonEnv(this, {
        label: "munos",
        input: this.input,
        asyncOps: this.asyncOps,
        persistence: this.persistence,
        // This tenant's private persistence scope, recomputed at env-build
        // time so it tracks the current deployment + script across hops.
        // `@`-prefixed namespaces route past it to the shared tier.
        persistenceScope: deriveScopeForPersistence(this.url, this.scriptLabel),
        maxClients: this.ctx.maxClients,
        romHash: this.ctx.romHash,
        screenWidth: this.ctx.screenW,
        screenHeight: this.ctx.screenH,
        onMemRead: (a, b, s) => this.onMemRead(a, b, s),
        onMemWrite: (a, v, b) => this.onMemWrite(a, v, b),
        onImageLiteral: (w, h, p) => this.onImageLiteral(w, h, p),
        onImageFromBytes: (p) => this.onImageFromBytes(p),
        onImageFromPngBytes: (p, x, y, w, h) => this.onImageFromPngBytes(p, x, y, w, h),
        onPaletteFromPngBytes: (p) => this.onPaletteFromPngBytes(p),
        onImageFromBitmapBytes: (p, dx, dy) => this.onImageFromBitmapBytes(p, dx, dy),
        onDrawCall: (call) => this.onDrawImage(call),
        // Sound: parse the inlined VGM into a tenant-private handle, then
        // voice it through the supervisor's shared audio (docs/sound.md).
        onSoundFromVgm: (p) => this.onSoundFromVgm(p),
        onSoundPlay: (call) => this.onSoundPlay(call),
        // ROM pause is a shared-game effect: route through the supervisor's
        // refcount. The one-shot guard keeps this tenant's contribution at
        // most one regardless of how often the script calls pause/resume.
        pauseRom: () => this.acquireSelfPause(),
        resumeRom: () => this.releaseSelfPause(),
        // Battery-save write-back toggle — a shared-game latch on the
        // supervisor (docs/battery-save-persistence.md).
        setBatteryPersist: (enabled) => this.ctx.setBatteryPersist(enabled),
        // Execution hooks act on the shared core (via the supervisor). The
        // on_exec handler re-enters THIS tenant's wasm — the tenant supplies
        // the `fire` closure so the core trap dispatches to its own
        // __cb_on_exec, never a sibling's.
        onExecHook: (target, cbId, priority) =>
          this.ctx.onExecHook(target, (pc) => this.invokeExecCallback(cbId, pc), priority),
        clearExecHook: (handle) => this.ctx.clearExecHook(handle),
        onReadReg: (reg) => this.ctx.readReg(reg),
        onWriteReg: (reg, val) => this.ctx.writeReg(reg, val),
        onWriteRom: (coord, byte) => this.ctx.writeRom(coord, byte),
        onPpuRead: (addr) => this.ctx.readPpu(addr),
        onPpuWrite: (addr, value) => this.ctx.writePpu(addr, value),
      }),

      // ----- transport -----

      send_client: (channel: number, dataPtr: number, reliable: number) => {
        // Stamp the shared-bus channel as a leading u16 so the server (and any
        // co-listener) routes by channel, not size.
        const body = this.readBytesPtr(dataPtr);
        const data = new Uint8Array(body.length + 2);
        data[0] = channel & 0xff;
        data[1] = (channel >> 8) & 0xff;
        data.set(body, 2);
        this.bus?.send(data, reliable !== 0);
      },
      register_channel: (channel: number) => {
        this.channels.add(channel & 0xffff);
      },

      hop: (urlPtr: number, handoffPtr: number) => {
        const url = resolveRedirectUrl(this.url, this.readStringPtr(urlPtr));
        const handoff = new Uint8Array(this.readBytesPtr(handoffPtr));
        console.log(`[munos] hop called: ${url} (handoff ${handoff.length} bytes)`);
        // A group hop: re-point the whole shared bus, not just this tenant
        // (docs/multi-script-client.md §"Hopping"). Defer past the current
        // wasm call stack — we may be inside the script's input/event handler.
        queueMicrotask(() => { this.ctx.hopGroup(url, handoff); });
      },
      redirect: () => {
        console.error("munos script error: redirect() called in client-side code");
      },
      disconnect: () => {
        console.log("[munos] disconnect() called — going offline");
        queueMicrotask(() => { this.performDisconnect(); });
      },

      // Multi-script: run another mod as this tenant's child over the same
      // game, or tear one down. Both are scoped to this tenant's subtree —
      // a script can never name a sibling. The handoff bytes are copied out
      // of wasm memory now (the spawn's source fetch resolves later, after
      // memory may have grown). See docs/multi-script-client.md.
      spawn_local: (urlPtr: number, handoffPtr: number, initDataPtr: number) => {
        // Resolve the same way hop/redirect do: an absolute ws/wss URL is
        // used as-is, a server-relative path (`/mod-name`) is resolved
        // against this tenant's own connection URL. This lets an
        // orchestrator (e.g. the lobby) spawn library mods by bare path
        // without hard-coding the deployment's origin.
        //
        // A `local:` prefix (e.g. `local:/smb_recorder`) marks a SINGLEPLAYER
        // launch: the mod runs on the in-browser LocalServer's shared bus
        // instead of going to the origin server. The prefix is stripped before
        // resolving, so the source still fetches from — and scopes its
        // persistence to — the real origin; only the bus is local.
        const raw = this.readStringPtr(urlPtr);
        const local = raw.startsWith("local:");
        const path = local ? raw.slice("local:".length) : raw;
        const url = resolveRedirectUrl(this.url, path);
        const handoff = new Uint8Array(this.readBytesPtr(handoffPtr));
        // init_data is an OPTIONAL param: an omitted arg lowers to pointer 0
        // (expression-codegen), never a real heap allocation, so treat 0 as
        // "no seed". A non-zero pointer is the per-instance blob → event init.
        const initData = initDataPtr !== 0
          ? new Uint8Array(this.readBytesPtr(initDataPtr))
          : new Uint8Array(0);
        return this.ctx.spawnTenant(url, handoff, this, local, initData);
      },
      despawn: (handle: number) => {
        this.ctx.despawnTenant(handle, this);
      },

      fetch: (urlPtr: number, cbId: number, ctx: number) => {
        const url = this.readStringPtr(urlPtr);
        const epoch = this.asyncOps.epochNow();
        // track() the whole chain so drain() awaits this fetch to quiescence
        // before it returns, exactly as the db_* builtins do (env-bindings.ts).
        // Without it, drain() doesn't consider the fetch "in flight", so a fetch
        // issued alongside a db_* op in one handler could have its callback
        // delivered a whole event later — out of issue order. This is the ONLY
        // async fetch binding; the server/reference/rig-dummy bindings push
        // their completion synchronously (no promise to await), so they already
        // deliver in issue order and need no track — keeping every tier's
        // ordering identical.
        this.asyncOps.track(
          this.fetchAdapter.fetch(url).then(result => {
            this.asyncOps.pushFetch(epoch, {
              cbId, ctx, url,
              success: result.success,
              data: result.data,
              ...(result.errorMsg !== undefined ? { errorMsg: result.errorMsg } : {}),
            });
          }).catch(e => {
            const msg = e instanceof Error ? e.message : String(e);
            this.asyncOps.pushFetch(epoch, {
              cbId, ctx, url,
              success: false,
              data: new Uint8Array(0),
              errorMsg: `internal_error: ${msg}`,
            });
          }),
        );
      },
    };
  }

  // ----- pause plumbing -----

  private acquireSelfPause(): void {
    if (this.selfPauseRequested) return;
    this.selfPauseRequested = true;
    this.ctx.acquirePause();
  }
  private releaseSelfPause(): void {
    if (!this.selfPauseRequested) return;
    this.selfPauseRequested = false;
    this.ctx.releasePause();
  }

  // Forget any ROM-pause this tenant holds and release its contribution to the
  // shared refcount. Called by the host's forced reset (resetRom) so a tenant's
  // one-shot guard can't desync from the zeroed refcount — otherwise the guard
  // stays set and the tenant's next pause_rom is a silent no-op. Idempotent;
  // a tenant holding nothing is untouched.
  resetPause(): void {
    this.releaseSelfPause();
  }

  // ----- gamepad input (called by the supervisor's input fan-out) -----
  //
  // Each fires event_client_input(button, pressed) synchronously and
  // returns whether THIS tenant consumed it. The supervisor ORs the
  // results across tenants: the ROM sees the edge only if no tenant
  // consumed it.

  pressButton(button: number): boolean {
    return this.input.press(button);
  }
  releaseButton(button: number): boolean {
    return this.input.release(button);
  }

  // ----- memory hooks: route through the shared core's CPU RAM -----

  protected onMemRead(addr: number, bytes: number, signed: boolean): number {
    return composeMemRead(a => this.ctx.readCpuByte(a & 0xffff), addr, bytes, signed);
  }
  protected onMemWrite(addr: number, value: number, bytes: number): void {
    spreadMemWrite((a, v) => this.ctx.writeCpuByte(a & 0xffff, v), addr, value, bytes);
  }

  // ----- image hooks: decode + stash indexed pixels keyed by wasm ptr -----

  protected onImageLiteral(w: number, h: number, dataPtr: number): number {
    const { pixels, error } = decodeImageLiteral(this.readStringPtr(dataPtr), w, h);
    if (error !== null) {
      console.error(`munos: image_literal bad base64 (${error}); zero-filling`);
    }
    return this.images.alloc(this, w, h, pixels);
  }

  protected onImageFromBytes(bytesPtr: number): number {
    const decoded = decodeImageWireBytes(this.readBytesPtr(bytesPtr));
    return this.images.alloc(this, decoded.width, decoded.height, decoded.pixels);
  }

  private loadPngBytes(bytesPtr: number): IndexedPng | null {
    // Read the bytes first: the cache is keyed by their CONTENT, not the wasm
    // pointer (the per-event bump arena reuses pointers, so a pointer-keyed
    // cache returns a stale image once a reused address holds different bytes).
    let bytes: Uint8Array;
    try {
      bytes = this.readBytesPtr(bytesPtr);
    } catch (e) {
      console.error(`munos: image_from_png_bytes failed to read bytes (${(e as Error).message})`);
      return null;
    }
    const key = pngContentKey(bytes);
    const cached = this.pngBytesCache.get(key);
    if (cached) return cached;
    let entry: IndexedPng;
    try {
      entry = decodePngToIndexed(bytes);
    } catch (e) {
      const head = bytes.slice(0, 16);
      const tail = bytes.slice(Math.max(0, bytes.length - 16));
      const hex = (b: Uint8Array) => Array.from(b).map(x => x.toString(16).padStart(2, "0")).join(" ");
      console.error(
        `munos: image_from_png_bytes decode failed (${(e as Error).message})\n` +
        `  ptr=0x${bytesPtr.toString(16)} len=${bytes.length}\n` +
        `  head: ${hex(head)}\n` +
        `  tail: ${hex(tail)}`,
      );
      return null;
    }
    // Bound the cache: evict the oldest entry once it's full so a per-frame-
    // varying source can't grow it without limit.
    if (this.pngBytesCache.size >= MAX_PNG_CACHE) {
      const oldest = this.pngBytesCache.keys().next().value;
      if (oldest !== undefined) this.pngBytesCache.delete(oldest);
    }
    this.pngBytesCache.set(key, entry);
    return entry;
  }

  protected onImageFromPngBytes(
    dataPtr: number, x: number, y: number, w: number, h: number,
  ): number {
    // Bound w*h before the extractIndexedRegion / zero-fill allocation below,
    // which sizes a host buffer from these raw i32 args (images.alloc also
    // checks, but its pixel argument is built first).
    assertImageDims(w, h);
    const png = this.loadPngBytes(dataPtr);
    if (!png) return this.images.alloc(this, w, h, new Uint8Array(w * h));
    return this.images.alloc(this, w, h, extractIndexedRegion(png, x, y, w, h));
  }

  protected onPaletteFromPngBytes(dataPtr: number): number {
    const png = this.loadPngBytes(dataPtr);
    if (!png) return this.allocArray(0, 4);
    const len = png.palette.length;
    const ptr = this.allocArray(len, 4);
    const data = arrData(this, ptr);
    for (let i = 0; i < len; i++) {
      this.view.setUint32(data + i * 4, png.palette[i]!, true);
    }
    return ptr;
  }

  protected onImageFromBitmapBytes(dataPtr: number, dx: number, dy: number): number {
    let bytes: Uint8Array;
    try {
      bytes = this.readBytesPtr(dataPtr);
    } catch (e) {
      console.error(`munos: image_from_bitmap_bytes failed to read bytes (${(e as Error).message})`);
      return this.images.alloc(this, 0, 0, new Uint8Array(0));
    }
    const decoded = decodeBitmap(bytes);
    if (!decoded) {
      console.error(`munos: image_from_bitmap_bytes decode failed (len=${bytes.length})`);
      return this.images.alloc(this, 0, 0, new Uint8Array(0));
    }
    return this.images.alloc(
      this, decoded.width, decoded.height, decoded.pixels, dx, dy,
    );
  }

  // ----- draw calls -----

  protected onDrawImage(call: DrawCall): void {
    this.pendingDrawCalls.push(call);
  }

  // ----- sound hooks (docs/sound.md) -----
  //
  // sound_from_vgm parses the inlined register-log once and hands back a
  // tenant-private handle; play_sound resolves it and routes the parsed
  // file to the supervisor, which owns the single shared audio voice. Sound
  // is data, not samples — the synth reconstructs it from the chip.

  protected onSoundFromVgm(dataPtr: number): number {
    let bytes: Uint8Array;
    try {
      bytes = this.readBytesPtr(dataPtr);
    } catch (e) {
      console.error(`munos: sound_from_vgm failed to read bytes (${(e as Error).message})`);
      return 0;
    }
    let file: VgmFile;
    try {
      file = parseVgm(bytes);
    } catch (e) {
      console.error(`munos: sound_from_vgm could not parse VGM (${(e as Error).message})`);
      return 0;
    }
    this.sounds.push(file);
    return this.sounds.length; // 1-based handle; 0 stays the null handle
  }

  protected onSoundPlay(call: SoundCall): void {
    const file = this.sounds[call.snd - 1];
    if (!file) {
      console.error(`munos: play_sound got an unknown sound handle ${call.snd}`);
      return;
    }
    this.ctx.playSound(file, call.loop);
  }

  // Composite this tenant's draw calls onto the shared framebuffer, then
  // snapshot + clear for the next frame. The supervisor calls this once
  // per tenant per frame in load order, so a later-added tenant paints on
  // top of an earlier one (docs/multi-script-client.md §Rendering).
  paintInto(
    framebuffer: Uint32Array, screenW: number, screenH: number,
    bgMask: Uint8Array | null = null,
  ): void {
    paintDrawCalls(this, framebuffer, screenW, screenH, this.images, this.pendingDrawCalls, bgMask);
    this.pendingDrawCalls = [];
  }

  // ----- per-frame entry (driven by the supervisor) -----

  // Snapshot input + fire event_client_frame. Called once per tenant per
  // frame by the supervisor (at OAM-DMA time, with a post-frame fallback).
  fireFrame(): void {
    if (this.scriptUnloaded) return;
    this.input.beginFrame();
    if (this.callEvent("event_client_frame", this.frameNum)) {
      this.frameNum++;
    }
  }

  // ----- transport: attach to the shared bus -----

  // Join the supervisor's bus for our current URL (it creates one for the
  // first rider and reuses it for the rest). A teardown that raced start()
  // can resume here already stopped/unloaded — don't attach then.
  protected connect(): Promise<void> {
    if (this.stopped || this.scriptUnloaded) return Promise.resolve();
    // A localBus tenant rides the shared in-browser LocalServer bus, not one
    // keyed by its own URL — that's how a launched singleplayer set co-locates
    // on one bus so a recorder can hear the sender (docs/multi-script-client.md).
    // A joinBus tenant likewise rides one shared bus, but to a remote P2P host,
    // so a joiner's whole mod group is one peer (docs/p2p.md). localBus wins if
    // both are somehow set (a local host can't also be a remote joiner).
    const busUrl = this.localBus ? LOCAL_BUS_KEY : this.joinBus ? JOIN_BUS_KEY : this.url;
    this.bus = this.ctx.attachToBus(this, busUrl);
    return Promise.resolve();
  }

  // ----- rehome: re-point this surviving tenant onto a new endpoint -----
  //
  // The state-preserving half of a group hop (docs/multi-script-client.md
  // §"Hopping"). The supervisor has determined this tenant's script is still
  // served at the destination byte-for-byte, so it KEEPS its wasm — every
  // global, every loaded image, the frame counter — and only swaps which bus
  // it rides: leave the old bus, adopt the new URL + redirect handoff, join the
  // new bus. (Scripts no longer served are evicted by the supervisor instead;
  // newly-served ones are started fresh. This tenant just carries on.)
  rehome(url: string, handoff: Uint8Array): void {
    if (this.stopped || this.scriptUnloaded) return;
    this.detachTransport();
    this.url = url;
    this.handoffBytes = handoff;
    void this.connect();
  }

  // Re-point this surviving tenant onto THIS browser's in-page LocalServer
  // loopback — the host_locally handoff (munos-script-host.reconcileHostLocally,
  // docs/node-federation.md). Unlike rehome(), the destination isn't a new URL:
  // it's the local bus. So we KEEP the tenant's real origin `url` — modUrl and
  // the persistence scope stay a fetchable origin, so hostedModUrls() still
  // advertises it and a P2P joiner (or a couch [+] tile) can fetch this same
  // client half — and only flip `localBus` so connect() rides LOCAL_BUS_KEY.
  // (rehome(LOCAL_BUS_KEY, …) would clobber `url` with the bus key, which
  // stranded onLocalBus=false + a non-fetchable modUrl and broke both joins.)
  rehomeLocal(handoff: Uint8Array): void {
    if (this.stopped || this.scriptUnloaded) return;
    this.detachTransport();
    this.localBus = true;
    this.handoffBytes = handoff;
    void this.connect();
  }

  // ----- async drain -----

  protected drainPendingDb(): void {
    if (this.scriptUnloaded) return;
    void this.asyncOps.drain();
  }
}

// The private persistence scope for a browser-fetched script:
// `<origin>::<script-id>` (see docs/persistence.md §"What identifies a
// script"). Two levels:
//   • origin — the deployment / trust boundary (forging it needs a
//     DNS/host compromise). This is the part that walls strangers apart.
//   • script-id — WHICH script within that deployment: the .munos
//     basename the server reports (in the source-fetch JSON envelope),
//     lowercased and extension-stripped to match the server-side scope. This is what
//     distinguishes a gateway from the scripts it spawns — and, here, one
//     mod from another tenant on the same deployment.
// The URL *path* is deliberately NOT used: it identifies an endpoint (a
// transient spawn instance), not a script. Origin falls back to the raw
// URL if parsing fails (e.g. a relative dev path).
function deriveScopeForPersistence(wsUrl: string, scriptLabel: string): string {
  const id = scriptLabel.replace(/\.munos$/i, "").toLowerCase();
  let origin: string;
  try {
    origin = new URL(wsUrl).origin;
  } catch {
    origin = wsUrl;
  }
  return `${origin}::${id}`;
}

// resolveRedirectUrl now lives in @multinostalgia/munos (next to the redirect
// codec) so the browser host and the fidelity-rig client resolve identically.
// Re-exported here to keep its existing import path (script-tenant.test.ts).
export { resolveRedirectUrl };
